import { 
  AfterViewInit, 
  ChangeDetectorRef, 
  Component, 
  ElementRef, 
  EventEmitter, 
  Input, 
  OnChanges, 
  OnInit, 
  Output, 
  signal, 
  ViewChild,
  Renderer2,
  OnDestroy 
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-locations',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.css']
})
export class LocationsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  locations: any[] = [];
  filteredLocations: any = signal([]);
  form: FormGroup;
  isValid = false;
  @Input() disabled = false;
  @Input() resetTimer = false;
  @Input() defaultValue: any = null;
  @Input() label = '¿Dónde vives?';
  @Input() timeoutSeconds = 8; // Cambiado de timerMs a timeoutSeconds
  @Output() valueState = new EventEmitter<number | null>();
  @ViewChild('location') location!: ElementRef;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;

  // Signals para control de timeout
  offTimeOut = signal(false);
  hasWritten = signal(false);
  private errorDebounceTimer: any;
  private countdownTimer: any;
  private viewInitialized = signal(false);

  filterTimeout: any;
  selectedLocation: any = null;
  isLoading = false;

  constructor(
    private listService: ListsService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    this.form = new FormGroup({
      location: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    // ✅ Control de timeout con la nueva lógica
    this.startCountdown();

    this.listService.getUbication("")
      .subscribe({
        next: (response: any) => {
          this.locations = response.ubications;
        },
      });

    if (this.disabled) {
      this.form.get('location')!.disable();
      this.getStyleTextDisabled();
    }

    // Escuchar cambios del formulario
    this.form.valueChanges.subscribe(() => {
      this.hasWritten.set(true);
      this.resetErrorDebounce();
      
      // Si el formulario es válido, limpiar timeout
      if (this.form.valid && this.form.get('location')?.value) {
        this.clearCountdown();
        this.offTimeOut.set(false);
        this.getStyleEnabled();
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
    
    // ✅ Emitir valor por defecto sin causar error
    if (this.defaultValue) {
      this.valueState.emit(this.defaultValue);
      this.cdr.detectChanges();
    }
  }

  ngOnChanges(): void {
    if (this.resetTimer) {
      this.clearCountdown();
      this.offTimeOut.set(false);
      this.startCountdown();
    }

    if (this.defaultValue) {
      this.findLocationById(this.defaultValue);
    }

    if (this.disabled) {
      this.form.get('location')!.disable();
      this.getStyleTextDisabled();
    } else {
      this.form.get('location')!.enable();
      this.getStyleEnabled();
    }
  }

  private applyInitialStyles() {
    if (this.disabled) {
      this.getStyleTextDisabled();
    } else {
      this.getStyleEnabled();
    }
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  // Métodos para el control de timeout y estilos
  startCountdown() {
    this.clearCountdown();
    this.countdownTimer = setTimeout(() => {
      if (!this.form.valid && !this.hasWritten() && !this.form.get('location')?.value) {
        this.offTimeOut.set(true);
        this.getStyleError();
        this.cdr.detectChanges();
      }
    }, this.timeoutSeconds * 1000);
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  resetErrorDebounce() {
    if (this.errorDebounceTimer) {
      clearTimeout(this.errorDebounceTimer);
    }

    this.errorDebounceTimer = setTimeout(() => {
      if (this.form.invalid && (this.form.touched || this.form.dirty) || this.offTimeOut()) {
        this.getStyleError();
      } else if (!this.disabled) {
        this.getStyleEnabled();
      }
    }, 1700);
  }

  getStyleError(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error')
      }
    });
  }
  
  getStyleEnabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled')
      }
    });
  }

  getStyleTextDisabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled')
      }
    });
  }

  findLocationById(id: any) {
    this.listService.getUbicationById(id).subscribe({
      next: (response: any) => {
        const ubication = response.ubications;
        this.setLocationDefaultValue(ubication.name);
      }
    });
  }

  setLocationDefaultValue(defaultValue: string) {
    this.form.get('location')!.setValue(defaultValue);
    if (this.defaultValue) {
      this.isValid = true;
      this.clearCountdown();
      this.offTimeOut.set(false);
      this.getStyleEnabled();
    }
    const locationObj = this.locations.find(location => location.id == this.defaultValue);
    if (locationObj && this.location) {
      this.location.nativeElement.value = locationObj.name;
      this.cdr.detectChanges();
    }
  }

  filterLocations(event: any) {
    this.isValid = false;
    const query = event.target.value.toLowerCase();
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.isLoading = true;
    this.filterTimeout = setTimeout(() => {
      this.listService.getUbication(query)
        .subscribe({
          next: (response: any) => {
            this.locations = response.ubications;
            this.filteredLocations.set(
              this.locations.filter((location: any) => {
                const wordsArray = query.split(' ');
                const locationLower = location.name.toLowerCase();
                return wordsArray.every((word:any) => locationLower.includes(word));
              })
            );
            this.isLoading = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.isLoading = false;
          }
        });
    }, 1050);
  }

  selectLocation(idLocation: string | number, location: string) {
    this.form.get('location')!.setValue(location);
    this.isValid = true;
    this.filteredLocations.set([]);
    this.valueState.emit(Number(idLocation));
    
    // Limpiar timeout cuando se selecciona una ubicación válida
    this.clearCountdown();
    this.offTimeOut.set(false);
    this.getStyleEnabled();
    
    this.cdr.detectChanges();
  }

  esc() {
    this.filteredLocations.set([]);
    this.form.get('location')!.setValue(null);
    this.isValid = false;
    this.valueState.emit(null);
    
    // Reiniciar el countdown si se limpia la selección
    if (!this.defaultValue) {
      this.startCountdown();
    }
  }

  makeInvalid() {
    this.isValid = false;
    this.valueState.emit(null);
  }

  validateBlur() {
    setTimeout(() => {
      if (!this.isValid) {
        this.esc();
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  hasError = () => {
    return this.offTimeOut() && !this.isValid && !this.form.get('location')?.value;
  }

  ngOnDestroy() {
    if (this.errorDebounceTimer) {
      clearTimeout(this.errorDebounceTimer);
    }
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.clearCountdown();
  }
}